import api from './api';

/**
 * Radiology Configuration Framework Service
 * Supports hierarchical configuration resolution:
 * SYSTEM DEFAULT -> CENTRE -> DEPARTMENT -> DOCTOR -> DOCTOR + INVESTIGATION -> TRANSACTION OVERRIDE
 */
const radiologyConfigService = {
  /**
   * Resolve effective configuration from backend with full precedence hierarchy
   */
  async resolveConfiguration(params = {}) {
    const { centreId, departmentId, doctorId, investigationId } = params;
    const res = await api.get('/radiology/config/resolve', {
      params: { centreId, departmentId, doctorId, investigationId }
    });
    return res.data;
  },

  /**
   * Resolve effective configuration including transaction override
   */
  async resolveConfigurationWithOverride(params = {}, transactionOverride = null) {
    const { centreId, departmentId, doctorId, investigationId } = params;
    const res = await api.post('/radiology/config/resolve-override', transactionOverride, {
      params: { centreId, departmentId, doctorId, investigationId }
    });
    return res.data;
  },

  /**
   * Fetch doctor preference profile
   */
  async getDoctorProfile(doctorId) {
    const res = await api.get(`/radiology/config/doctor/${doctorId}`);
    return res.data;
  },

  /**
   * Save doctor preference profile
   */
  async saveDoctorProfile(doctorId, preferences) {
    const res = await api.post(`/radiology/config/doctor/${doctorId}`, preferences);
    return res.data;
  },

  /**
   * Fetch doctor + investigation configuration (e.g. Dr. A + CT Brain)
   */
  async getDoctorInvestigationConfig(doctorId, investigationId) {
    const res = await api.get(`/radiology/config/doctor/${doctorId}/investigation/${investigationId}`);
    return res.data;
  },

  /**
   * Save doctor + investigation configuration
   */
  async saveDoctorInvestigationConfig(doctorId, investigationId, preferences) {
    const res = await api.post(`/radiology/config/doctor/${doctorId}/investigation/${investigationId}`, preferences);
    return res.data;
  },

  /**
   * Fetch centre configuration
   */
  async getCentreConfig(centreId) {
    const res = await api.get(`/radiology/config/centre/${centreId}`);
    return res.data;
  },

  /**
   * Save centre configuration
   */
  async saveCentreConfig(centreId, preferences) {
    const res = await api.post(`/radiology/config/centre/${centreId}`, preferences);
    return res.data;
  },

  /**
   * Save generic configuration entry
   */
  async saveConfiguration(payload) {
    const res = await api.post('/radiology/config', payload);
    return res.data;
  },

  /**
   * Query PACS study status via the architectural integration boundary
   */
  async getPacsStatus(testId, accessionNumber = null) {
    const params = accessionNumber ? { accessionNumber } : {};
    const res = await api.get(`/radiology/pacs/status/${testId}`, { params });
    return res.data;
  }
};

export default radiologyConfigService;
